import React, { useState, useEffect, useMemo } from 'react';
import { IdentityService, SessionStorage, Table, createTextColumn, useTableState, useAuth, UserAbility, Product, MASTER_CLINIC_ID, MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle } from '@medprompt/shared';
import { sharedApiClient, InsightsService, NotificationService } from './fetcher';
import HelpButton from './help/HelpButton';
import { HELP_PAGE_IDS } from './help/helpContent';
import './list-view-page.css';

const numericCompare = (accessor) => (a, b) => {
  const valueA = accessor(a);
  const valueB = accessor(b);
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return 1;
  if (valueB == null) return -1;
  return valueA - valueB;
};

const naRenderer = (value) => (value == null ? 'N.A.' : value);

const clinicHasProduct = (clinic, productKey) =>
  Array.isArray(clinic?.products) && clinic.products.includes(productKey);

const ClinicSelectorViewController = () => {
  const { user } = useAuth();
  const hasInsightsRead = !!user?.authorities?.includes(UserAbility.INSIGHTS_READ.key);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlightedRows, setHighlightedRows] = useState(new Set());

  const { sortState, setSortState } = useTableState('ClinicSelectorTable', {
    defaultSortState: { columnKey: 'assignedDocumentsCount', direction: 'desc' }
  });

  const anyClinicHasDocuments = useMemo(
    () => clinics.some((clinic) => clinic.clinicId !== MASTER_CLINIC_ID && clinicHasProduct(clinic, Product.DOCUMENTS.key)),
    [clinics]
  );
  const anyClinicHasScribe = useMemo(
    () => clinics.some((clinic) => clinic.clinicId !== MASTER_CLINIC_ID && clinicHasProduct(clinic, Product.SCRIBE.key)),
    [clinics]
  );

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.clinics);
  }, []);

  useEffect(() => {
    const identityService = new IdentityService();

    identityService.fetchClinics()
      .then(async (data) => {
        const clinicList = data || [];
        setClinics(clinicList);

        const selectedId = SessionStorage.getSelectedClinicId();
        if (selectedId) {
          setHighlightedRows(new Set([selectedId]));
        }

        const insightsService = new InsightsService(sharedApiClient);
        const enriched = await Promise.all(clinicList.map(async (clinic) => {
          if (clinic.clinicId === MASTER_CLINIC_ID) {
            return {
              ...clinic,
              documentsReadyForReview: null,
              documentsNeverOpened: null,
              consultationsReadyForReview: null,
              assignedDocumentsCount: null,
              assignedTasksCount: null
            };
          }

          const headerOverride = { headers: { 'X-Target-Clinic-Id': clinic.clinicId } };
          const hasDocuments = clinicHasProduct(clinic, Product.DOCUMENTS.key);
          const hasScribe = clinicHasProduct(clinic, Product.SCRIBE.key);
          let documentState = {};
          let consultationState = {};
          let notifications = {};

          if (hasInsightsRead && hasDocuments) {
            try {
              documentState = await insightsService.fetchCurrentProcessorState(headerOverride);
            } catch (e) {
              console.error(`Failed to fetch document state for clinic ${clinic.clinicId}:`, e);
            }
          }

          if (hasInsightsRead && hasScribe) {
            try {
              consultationState = await insightsService.fetchCurrentConsultationsState(headerOverride);
            } catch (e) {
              console.error(`Failed to fetch consultation state for clinic ${clinic.clinicId}:`, e);
            }
          }

          try {
            notifications = await sharedApiClient.get('/api/notification/assigned/fetch', headerOverride);
          } catch (e) {
            console.error(`Failed to fetch notifications for clinic ${clinic.clinicId}:`, e);
          }

          return {
            ...clinic,
            documentsReadyForReview: hasDocuments ? (documentState.documentsReadyForReview ?? 0) : null,
            documentsNeverOpened: hasDocuments ? (documentState.documentsNeverOpened ?? 0) : null,
            consultationsReadyForReview: hasScribe ? (consultationState.consultationsReadyForReview ?? 0) : null,
            assignedDocumentsCount: notifications.assignedDocumentsCount ?? 0,
            assignedTasksCount: notifications.assignedTasksCount ?? 0
          };
        }));

        setClinics(enriched);
      })
      .catch((error) => {
        console.error('Failed to fetch clinics:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hasInsightsRead]);

  const columns = useMemo(() => {
    const cols = [
      createTextColumn('clinicName', 'Clinic Name', (c) => c.clinicName),
      createTextColumn('clinicId', 'Clinic ID', (c) => c.clinicId),
    ];

    if (hasInsightsRead && anyClinicHasDocuments) {
      cols.push(
        createTextColumn('documentsReadyForReview', 'Documents Ready for Review', (c) => c.documentsReadyForReview, {
          filterable: false,
          renderer: naRenderer,
          compareFn: numericCompare((c) => c.documentsReadyForReview)
        }),
        createTextColumn('documentsNeverOpened', 'Documents Never Opened', (c) => c.documentsNeverOpened, {
          filterable: false,
          renderer: naRenderer,
          compareFn: numericCompare((c) => c.documentsNeverOpened)
        })
      );
    }

    cols.push(
      createTextColumn('assignedDocumentsCount', 'Documents Assigned', (c) => c.assignedDocumentsCount, {
        filterable: false,
        renderer: naRenderer,
        compareFn: numericCompare((c) => c.assignedDocumentsCount)
      })
    );

    if (hasInsightsRead && anyClinicHasScribe) {
      cols.push(
        createTextColumn('consultationsReadyForReview', 'Consultations Ready for Review', (c) => c.consultationsReadyForReview, {
          filterable: false,
          renderer: naRenderer,
          compareFn: numericCompare((c) => c.consultationsReadyForReview)
        })
      );
    }

    cols.push(
      createTextColumn('assignedTasksCount', 'Tasks Assigned', (c) => c.assignedTasksCount, {
        filterable: false,
        renderer: naRenderer,
        compareFn: numericCompare((c) => c.assignedTasksCount)
      })
    );

    return cols;
  }, [hasInsightsRead, anyClinicHasDocuments, anyClinicHasScribe]);

  const handleRowClick = (clinic) => {
    SessionStorage.setSelectedClinicId(clinic.clinicId);
    setHighlightedRows(new Set([clinic.clinicId]));
  };

  if (loading) {
    return (
      <div className="list-view-page clinic-selector-view">
        <div className="loading-container">Loading clinics...</div>
      </div>
    );
  }

  return (
    <div className="list-view-page clinic-selector-view">
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.clinics}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.clinics} />
          </div>
        </div>
        <div className="header-buttons" />
      </div>
      <Table
        id="ClinicSelectorTable"
        data={clinics}
        columns={columns}
        getRowKey={(clinic) => clinic.clinicId}
        sortState={sortState}
        onSortChange={setSortState}
        onRowClick={handleRowClick}
        highlightedRows={highlightedRows}
        onHighlightChange={setHighlightedRows}
        emptyMessage="No clinics available."
      />
    </div>
  );
};

export default ClinicSelectorViewController;
